import type { AccountLastTask, TaskDue } from '@/lib/types';

/*
 * The account-wide "latest task" — wording and pure helpers, the twin of
 * `./account-last-note`. ONE copy for `/companies`.
 *
 * ⛔ NOTHING HERE FETCHES OR WRITES. The task arrives on the `/companies` list
 * row as `accountLastTask`; these functions only decide what may be said about it.
 *
 * Three states, and collapsing any two is a defect:
 *
 * | value       | means                                        |
 * | ----------- | -------------------------------------------- |
 * | `undefined` | NOT REPORTED — an older server; say nothing  |
 * | `null`      | the account has NO task anywhere             |
 * | an object   | the task; title/assignee only when non-null  |
 */

/** Column heading for the task's title, state and due time. */
export const ACCOUNT_LAST_TASK_LABEL = 'Latest task';

/** Column heading for when that task was added. */
export const ACCOUNT_LAST_TASK_AT_LABEL = 'Last task added';

/** The account has no task at all (`null`). */
export const ACCOUNT_NO_TASKS = 'No tasks';

/** The reader lacks `tasks.read`: the date and state are visible, the title is not. */
export const ACCOUNT_TASK_NOT_VISIBLE = 'Not visible to you';

/**
 * The task's due time, or `null`. Accepts `dueAt` or `due` and only a
 * {@link TaskDue}-shaped object, so a wire-name or shape mismatch renders no
 * chip rather than a broken one.
 */
export function accountLastTaskDue(task: AccountLastTask | null | undefined): TaskDue | null {
  if (!task) return null;
  const raw: unknown = task.dueAt ?? task.due ?? null;
  if (raw === null || typeof raw !== 'object') return null;
  const due = raw as Partial<TaskDue>;
  return typeof due.utc === 'string' && typeof due.timeZone === 'string' ? (raw as TaskDue) : null;
}

/** The task's `createdAt` (ISO datetime), or `null` for no task / not reported. */
export function accountLastTaskDate(task: AccountLastTask | null | undefined): string | null {
  return task ? task.createdAt : null;
}
